import { useMemo, useState } from 'react';
import { addSeconds, isBefore } from 'date-fns';

import { isOnboardingSurvey, Survey } from 'entities';

import { useAuth } from 'providers/Auth';

import { typedStorage } from 'utils/storage';
import { getTimestamp } from 'utils/date';
import { listGet } from 'utils/array';

interface ClickedIds {
  id: string;
  timestamp: string;
}

/**
 * Arbitrary to not clog our LS
 *
 * We'll not use this for now but leave it implemented
 * for when we have more control over the dashboard
 */
function isRecentClick({ timestamp }: ClickedIds) {
  // return isBefore(new Date(), addMinutes(new Date(timestamp), 7));
  return isBefore(new Date(), addSeconds(new Date(timestamp), 10));
}

export function useSurveys() {
  const { data, updateData, isLoadingSurveys } = useAuth();
  const { surveys, user } = data!;

  const clickStore = useMemo(() => {
    return typedStorage<{ clickedIds: ClickedIds[] }>().init({
      clickedIds: {
        key: `${user.id}_cc`,
        defaultValue: [] as ClickedIds[],
      },
    });
  }, [user.id]);

  const [surveyClicks, setClicks] = useState(
    () => clickStore.clickedIds.get()?.filter(isRecentClick) || [],
  );

  const onSurveyClick = (id: string) => {
    updateData({
      surveys: surveys.filter((s) => s.id !== id),
    });

    if (isOnboardingSurvey(id)) return;

    setClicks((old) => {
      const list = old || [];

      const updated = [...list.filter(isRecentClick), { id, timestamp: getTimestamp() }];

      clickStore.clickedIds.set(updated);

      return updated;
    });
  };

  const overwriteSurveys = (surveys: Survey[]) => {
    updateData({ surveys });
  };

  const availableSurveys = surveys.filter((s) => !listGet(surveyClicks, s.id));

  return {
    isLoadingSurveys,
    availableSurveys,
    onSurveyClick,
    overwriteSurveys,
  };
}
